/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/landing/components/ProductShowcase.tsx
 * PURPOSE: Product Showcase section - Tabbed screenshots + demo video link
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * DATE: 2025-12-28
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System V2.0
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/LOGIN-LANDING-REDESIGN-DESIGN.md
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ProductShowcase.module.css";

interface Tab {
  id: string;
  labelKey: string;
}

const tabs: Tab[] = [
  { id: "dashboard", labelKey: "landing.productShowcase.tabs.dashboard" },
  { id: "attendance", labelKey: "landing.productShowcase.tabs.attendance" },
  { id: "scheduling", labelKey: "landing.productShowcase.tabs.scheduling" },
  { id: "bhxh", labelKey: "landing.productShowcase.tabs.bhxh" },
  { id: "analytics", labelKey: "landing.productShowcase.tabs.analytics" },
];

export const ProductShowcase: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <div className={styles.showcase}>
      <h2 className={styles.title}>{t("landing.productShowcase.title")}</h2>

      {/* Tabs */}
      <div className={styles.tabs} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Screenshot Preview */}
      <div className={styles.preview} role="tabpanel">
        <div className={styles.browser}>
          <div className={styles.browserBar}>
            <div className={styles.browserDots}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
            <div className={styles.browserUrl}>nqh-bot.app/{activeTab}</div>
          </div>
          <div className={styles.browserContent}>
            <div className={styles.previewLabel}>
              {t(`landing.productShowcase.preview.${activeTab}`)}
            </div>
          </div>
        </div>
      </div>

      {/* Demo Video Link */}
      <div className={styles.demoLink}>
        <a href="#contact" className={styles.videoLink}>
          ▶️ {t("landing.productShowcase.demoVideo")}
        </a>
      </div>
    </div>
  );
};
